<script setup lang="ts">
/**
 * The app's icon set, in one place.
 *
 * The top bar, the left rail and the mobile tab bar all show the same
 * concepts, so they draw from the same source — otherwise "Messages" ends up
 * looking different in three places.
 *
 * **These are Heroicons.** They used to be hand-drawn paths in this file,
 * which meant every new icon was a drawing exercise and the weights never
 * quite matched. Heroicons ships an outline and a solid of each at the same
 * 24px grid, which is exactly the shape `filled` already had: outline at rest,
 * solid for the active state, with no difference in size or alignment between
 * the two.
 *
 * `IconName` is this app's vocabulary, not Heroicons' — `MAP` is the only
 * place the two meet. A caller asks for `messages`, not for
 * `ChatBubbleLeftRightIcon`, so swapping an icon is one line here and nothing
 * anywhere else. That is also why the name list has not changed: nothing that
 * uses an icon had to be touched to move the whole set.
 */
import type { Component } from 'vue';
import { computed } from 'vue';

import {
  ArrowPathRoundedSquareIcon as RepeatOutline,
  ArrowRightIcon as ArrowRightOutline,
  AtSymbolIcon as AtOutline,
  BellIcon as BellOutline,
  BookmarkIcon as BookmarkOutline,
  BuildingStorefrontIcon as StoreOutline,
  CalendarDaysIcon as CalendarOutline,
  ChartBarIcon as PollOutline,
  ChartPieIcon as InsightsOutline,
  ChatBubbleLeftRightIcon as MessagesOutline,
  CheckBadgeIcon as VerifiedOutline,
  CheckIcon as CheckOutline,
  ChevronDownIcon as ChevronOutline,
  Cog6ToothIcon as SettingsOutline,
  EllipsisHorizontalIcon as MoreOutline,
  EnvelopeIcon as MailOutline,
  EyeIcon as EyeOutline,
  EyeSlashIcon as EyeOffOutline,
  FaceSmileIcon as MoodOutline,
  FlagIcon as FlagOutline,
  GlobeAltIcon as GlobeOutline,
  HeartIcon as HeartOutline,
  HomeIcon as HomeOutline,
  LanguageIcon as TranslateOutline,
  LinkIcon as LinkOutline,
  LockClosedIcon as LockOutline,
  MagnifyingGlassCircleIcon as DiscoverOutline,
  MagnifyingGlassIcon as SearchOutline,
  MapPinIcon as PinOutline,
  MicrophoneIcon as MicOutline,
  MusicalNoteIcon as MusicOutline,
  PaperAirplaneIcon as SendOutline,
  PauseIcon as PauseOutline,
  PhoneIcon as PhoneOutline,
  PhoneXMarkIcon as PhoneOffOutline,
  PhotoIcon as ImageOutline,
  PlayIcon as PlayOutline,
  PuzzlePieceIcon as GameOutline,
  ShieldCheckIcon as ShieldOutline,
  SparklesIcon as InterestsOutline,
  UserCircleIcon as ProfileOutline,
  UserGroupIcon as FriendsOutline,
  UserPlusIcon as PersonAddOutline,
  VideoCameraIcon as VideoOutline,
  XMarkIcon as CloseOutline,
} from '@heroicons/vue/24/outline';

import {
  ArrowPathRoundedSquareIcon as RepeatSolid,
  ArrowRightIcon as ArrowRightSolid,
  AtSymbolIcon as AtSolid,
  BellIcon as BellSolid,
  BookmarkIcon as BookmarkSolid,
  BuildingStorefrontIcon as StoreSolid,
  CalendarDaysIcon as CalendarSolid,
  ChartBarIcon as PollSolid,
  ChartPieIcon as InsightsSolid,
  ChatBubbleLeftRightIcon as MessagesSolid,
  CheckBadgeIcon as VerifiedSolid,
  CheckIcon as CheckSolid,
  ChevronDownIcon as ChevronSolid,
  Cog6ToothIcon as SettingsSolid,
  EllipsisHorizontalIcon as MoreSolid,
  EnvelopeIcon as MailSolid,
  EyeIcon as EyeSolid,
  EyeSlashIcon as EyeOffSolid,
  FaceSmileIcon as MoodSolid,
  FlagIcon as FlagSolid,
  GlobeAltIcon as GlobeSolid,
  HeartIcon as HeartSolid,
  HomeIcon as HomeSolid,
  LanguageIcon as TranslateSolid,
  LinkIcon as LinkSolid,
  LockClosedIcon as LockSolid,
  MagnifyingGlassCircleIcon as DiscoverSolid,
  MagnifyingGlassIcon as SearchSolid,
  MapPinIcon as PinSolid,
  MicrophoneIcon as MicSolid,
  MusicalNoteIcon as MusicSolid,
  PaperAirplaneIcon as SendSolid,
  PauseIcon as PauseSolid,
  PhoneIcon as PhoneSolid,
  PhoneXMarkIcon as PhoneOffSolid,
  PhotoIcon as ImageSolid,
  PlayIcon as PlaySolid,
  PuzzlePieceIcon as GameSolid,
  ShieldCheckIcon as ShieldSolid,
  SparklesIcon as InterestsSolid,
  UserCircleIcon as ProfileSolid,
  UserGroupIcon as FriendsSolid,
  UserPlusIcon as PersonAddSolid,
  VideoCameraIcon as VideoSolid,
  XMarkIcon as CloseSolid,
} from '@heroicons/vue/24/solid';

export type IconName =
  | 'home'
  | 'discover'
  | 'friends'
  | 'messages'
  | 'notifications'
  | 'profile'
  | 'settings'
  | 'interests'
  | 'saved'
  | 'globe'
  | 'search'
  | 'chevron'
  | 'verified'
  | 'more'
  | 'calendar'
  | 'link'
  | 'shield'
  | 'at'
  | 'lock'
  | 'eye'
  | 'eye-off'
  | 'arrow-right'
  | 'mail'
  | 'check'
  | 'translate'
  | 'heart'
  | 'image'
  | 'poll'
  | 'mood'
  | 'person-add'
  | 'repeat'
  | 'insights'
  | 'store'
  | 'flag'
  | 'close'
  | 'music'
  | 'pin'
  | 'phone'
  | 'phone-off'
  | 'video'
  | 'mic'
  | 'send'
  | 'play'
  | 'game'
  | 'pause';

/**
 * This app's names on the left, Heroicons on the right.
 *
 * A few are worth saying out loud, because the obvious Heroicon is not the
 * right one:
 *
 *  - `discover` and `search` are both magnifiers in Heroicons, and they are
 *    two different destinations here — so Discover takes the circled one and
 *    the search field takes the plain one.
 *  - `chevron` points **down**, which is what it did before. Two callers
 *    rotate it by -90° to point right, and that only works if the base
 *    direction does not move.
 */
const MAP: Record<IconName, [Component, Component]> = {
  home: [HomeOutline, HomeSolid],
  discover: [DiscoverOutline, DiscoverSolid],
  friends: [FriendsOutline, FriendsSolid],
  messages: [MessagesOutline, MessagesSolid],
  notifications: [BellOutline, BellSolid],
  profile: [ProfileOutline, ProfileSolid],
  settings: [SettingsOutline, SettingsSolid],
  interests: [InterestsOutline, InterestsSolid],
  saved: [BookmarkOutline, BookmarkSolid],
  globe: [GlobeOutline, GlobeSolid],
  search: [SearchOutline, SearchSolid],
  chevron: [ChevronOutline, ChevronSolid],
  verified: [VerifiedOutline, VerifiedSolid],
  more: [MoreOutline, MoreSolid],
  calendar: [CalendarOutline, CalendarSolid],
  link: [LinkOutline, LinkSolid],
  shield: [ShieldOutline, ShieldSolid],
  at: [AtOutline, AtSolid],
  lock: [LockOutline, LockSolid],
  eye: [EyeOutline, EyeSolid],
  'eye-off': [EyeOffOutline, EyeOffSolid],
  'arrow-right': [ArrowRightOutline, ArrowRightSolid],
  mail: [MailOutline, MailSolid],
  check: [CheckOutline, CheckSolid],
  translate: [TranslateOutline, TranslateSolid],
  heart: [HeartOutline, HeartSolid],
  image: [ImageOutline, ImageSolid],
  poll: [PollOutline, PollSolid],
  mood: [MoodOutline, MoodSolid],
  'person-add': [PersonAddOutline, PersonAddSolid],
  repeat: [RepeatOutline, RepeatSolid],
  insights: [InsightsOutline, InsightsSolid],
  store: [StoreOutline, StoreSolid],
  flag: [FlagOutline, FlagSolid],
  close: [CloseOutline, CloseSolid],
  music: [MusicOutline, MusicSolid],
  pin: [PinOutline, PinSolid],
  // A chat bubble used to stand in for "Call" and a photo frame for "Video",
  // because the set had neither. Calling is real now and `phone` is the button
  // that starts one; `video` is still drawn and disabled, which is a reason to
  // mark it, not to mislabel it.
  phone: [PhoneOutline, PhoneSolid],
  // Hanging up, which needs to be unmistakably not "call". A phone with a
  // cross, the same glyph every other product uses for it.
  'phone-off': [PhoneOffOutline, PhoneOffSolid],
  video: [VideoOutline, VideoSolid],
  mic: [MicOutline, MicSolid],
  // The send button drew its own path: four points that read as a chevron
  // pointing the wrong way rather than as anything leaving. A paper plane at the
  // same weight as the rest of the set, from the set.
  send: [SendOutline, SendSolid],
  // A puzzle piece, not the chart that `poll` draws: a game and a poll are
  // different things and the bar chart reads as statistics.
  game: [GameOutline, GameSolid],
  play: [PlayOutline, PlaySolid],
  pause: [PauseOutline, PauseSolid],
};

const props = withDefaults(defineProps<{ name: IconName; filled?: boolean }>(), { filled: false });

const icon = computed(() => MAP[props.name][props.filled ? 1 : 0]);

/**
 * Every icon here is decoration, and says so.
 *
 * Heroicons set `aria-hidden="true"` inside their own render and spread
 * nothing, so an `aria-label` put on one of these never reaches the `<svg>`
 * — it is silently dropped, which is worse than not offering it. A control
 * that needs a name takes it on the **control**: an `aria-label` on the
 * button, or `sr-only` text beside the icon when there is no button to label.
 * Both of those are also findable by text, which an `aria-label` on an svg
 * is not.
 *
 * The template is one root node on purpose. An HTML comment counts as a node,
 * and with two roots Vue cannot decide where a fallthrough attribute belongs,
 * so it drops them — which silently took `class` off every icon in the app
 * for as long as this comment sat inside the template.
 */

</script>

<template>
  <component :is="icon" />
</template>
